import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
	Search,
	Map,
	LayoutGrid,
	SlidersHorizontal,
	X,
} from "lucide-react";
import { api } from "@/lib/api";
import { CATEGORY_LABELS } from "@/lib/types";
import type { Tool, User, ToolCategory } from "@/lib/types";
import { MapView } from "./MapView";
import { ToolCard } from "./ToolCard";

interface DiscoverPageProps {
	onSelectTool: (toolId: string) => void;
}

export function DiscoverPage({ onSelectTool }: DiscoverPageProps) {
	const [tools, setTools] = useState<(Tool & { owner?: User })[]>([]);
	const [search, setSearch] = useState("");
	const [category, setCategory] = useState<string>("");
	const [loading, setLoading] = useState(true);
	const [viewMode, setViewMode] = useState<"map" | "grid">("map");

	useEffect(() => {
		loadTools();
	}, [search, category]);

	async function loadTools() {
		setLoading(true);
		try {
			const params: { q?: string; category?: string } = {};
			if (search) params.q = search;
			if (category) params.category = category;
			const data = await api.getTools(params);
			setTools(data);
		} catch (e) {
			console.error("Failed to load tools:", e);
		} finally {
			setLoading(false);
		}
	}

	const categories = Object.entries(CATEGORY_LABELS);

	return (
		<div className="space-y-4">
			{/* Hero section */}
			<div className="text-center py-6">
				<h1 className="text-2xl font-bold tracking-tight">
					Borrow tools from your neighbors
				</h1>
				<p className="text-muted-foreground mt-1 text-sm">
					Save money, reduce waste, build community
				</p>
			</div>

			{/* Search bar */}
			<div className="relative max-w-xl mx-auto">
				<Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
				<Input
					placeholder="Search tools... (e.g., drill, lawn mower, ladder)"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="pl-10 pr-10"
				/>
				{search && (
					<button
						onClick={() => setSearch("")}
						className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
					>
						<X className="w-4 h-4" />
					</button>
				)}
			</div>

			{/* Category filters */}
			<div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
				<Badge
					variant={!category ? "default" : "outline"}
					className="cursor-pointer whitespace-nowrap shrink-0"
					onClick={() => setCategory("")}
				>
					All
				</Badge>
				{categories.map(([key, label]) => (
					<Badge
						key={key}
						variant={category === key ? "default" : "outline"}
						className="cursor-pointer whitespace-nowrap shrink-0"
						onClick={() => setCategory(category === key ? "" : key)}
					>
						{label}
					</Badge>
				))}
			</div>

			{/* View toggle */}
			<div className="flex items-center justify-between">
				<p className="text-sm text-muted-foreground">
					{tools.length} tool{tools.length !== 1 ? "s" : ""} available nearby
				</p>
				<div className="flex items-center gap-1 border border-border rounded-md p-0.5">
					<Button
						variant={viewMode === "map" ? "secondary" : "ghost"}
						size="sm"
						className="h-7 px-2 gap-1"
						onClick={() => setViewMode("map")}
					>
						<Map className="w-3.5 h-3.5" />
						Map
					</Button>
					<Button
						variant={viewMode === "grid" ? "secondary" : "ghost"}
						size="sm"
						className="h-7 px-2 gap-1"
						onClick={() => setViewMode("grid")}
					>
						<LayoutGrid className="w-3.5 h-3.5" />
						Grid
					</Button>
				</div>
			</div>

			{/* Content */}
			{loading ? (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
					{Array.from({ length: 8 }).map((_, i) => (
						<div
							key={i}
							className="h-64 bg-muted rounded-lg animate-pulse"
						/>
					))}
				</div>
			) : viewMode === "map" ? (
				<div className="space-y-4">
					<MapView tools={tools} onSelectTool={onSelectTool} />
					<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
						{tools.map((tool) => (
							<ToolCard
								key={tool.id}
								tool={tool}
								onClick={() => onSelectTool(tool.id)}
							/>
						))}
					</div>
				</div>
			) : (
				<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
					{tools.map((tool) => (
						<ToolCard
							key={tool.id}
							tool={tool}
							onClick={() => onSelectTool(tool.id)}
						/>
					))}
				</div>
			)}

			{!loading && tools.length === 0 && (
				<div className="text-center py-12">
					<p className="text-muted-foreground">No tools found</p>
					<p className="text-sm text-muted-foreground mt-1">
						Try adjusting your search or filters
					</p>
				</div>
			)}
		</div>
	);
}
